import {useMemo} from 'react';

import {ProgressData, ToolCallStatus} from 'llm/chat-library/progress/ProgressTypes';
import {parseProgressToolCall} from 'llm/chat-library/tool-details/ParsedProgressToolCall';
import {ToolType} from 'llm/chat-library/tool-details/types';

/**
 * The Pipelines Custom Code Writer agent does not persist its generated code into chat application
 * state; the code only exists as the `code` argument to the code-executing tool calls it makes. This
 * hook derives the most recent such code from the thread's progress tool calls so the editor can write
 * it back.
 *
 * We only read the pipeline-step execution tool. The generic code runner (RunCodeTool) is deliberately excluded because its code does
 * not match that format.
 */
const CODE_TOOL_TYPES: ReadonlySet<string> = new Set([ToolType.ExecuteCustomCodePipelineStep]);

export function getLatestGeneratedCustomCode(
  progressData: ProgressData | null,
  isAssistantRunning: boolean
): string | null {
  if (isAssistantRunning || progressData == null) {
    return null;
  }

  const orderedToolCalls = [...progressData.toolCalls].sort(
    (a, b) => b.createdAt.getTime() - a.createdAt.getTime()
  );

  for (const toolCall of orderedToolCalls) {
    if (!CODE_TOOL_TYPES.has(toolCall.tool)) {
      continue;
    }
    if (toolCall.status !== ToolCallStatus.COMPLETED) {
      continue;
    }
    const parsed = parseProgressToolCall(toolCall);
    if (
      parsed != null &&
      parsed.tool === ToolType.ExecuteCustomCodePipelineStep &&
      parsed.code.trim().length > 0
    ) {
      return parsed.code;
    }
  }

  return null;
}

/**
 * React hook wrapper around {@link getLatestGeneratedCustomCode}, memoized on the progress data.
 *
 * While the agent is still running the code is intermediate and must not be surfaced, so this returns
 * null until {@link isAssistantRunning} is false.
 */
export function useGeneratedCustomCode(
  progressData: ProgressData | null,
  isAssistantRunning: boolean
): string | null {
  return useMemo(
    () => getLatestGeneratedCustomCode(progressData, isAssistantRunning),
    [progressData, isAssistantRunning]
  );
}
